import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Play, Star, type LucideIcon } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import { cn } from '@/utils/cn';
import PlatformKeyVisual from './PlatformKeyVisual';
import { ShowcaseItemButton, type HomeShowcaseItem } from './PlatformShowcaseCard';
import type { HomePlatformTheme } from './homeThemes';

import 'swiper/css';

export interface HomeShowcaseSwiperAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}

interface PlatformShowcaseSwiperCardProps {
  theme: HomePlatformTheme;
  primaryAction: {
    label: string;
    onClick: () => void;
  };
  secondaryActions: HomeShowcaseSwiperAction[];
  items: HomeShowcaseItem[];
  itemLabel: string;
}

export default function PlatformShowcaseSwiperCard({
  theme,
  primaryAction,
  secondaryActions,
  items,
  itemLabel,
}: PlatformShowcaseSwiperCardProps) {
  const [activePage, setActivePage] = useState(0);
  const pages = useMemo(() => {
    const nextPages: HomeShowcaseItem[][] = [items.slice(0, 5)];
    const restItems = items.slice(5);
    for (let pageStart = 0; pageStart < restItems.length; pageStart += 15) {
      nextPages.push(restItems.slice(pageStart, pageStart + 15));
    }
    return nextPages.filter((pageItems) => pageItems.length > 0);
  }, [items]);
  const isExpanded = activePage > 0;
  const shouldShowScrollHint = activePage < pages.length - 1;

  useEffect(() => {
    if (activePage > pages.length - 1) {
      setActivePage(Math.max(0, pages.length - 1));
    }
  }, [activePage, pages.length]);

  const handleSlideChange = (swiper: SwiperInstance) => {
    setActivePage(swiper.activeIndex);
  };

  return (
    <article className={cn('overflow-hidden rounded-[20px] bg-gradient-to-br p-[1px] shadow-[0_16px_42px_rgba(2,6,23,0.34)]', theme.frame, theme.glow)}>
      <div className={cn('home-premium-surface relative overflow-hidden rounded-[19px] bg-gradient-to-br p-2.5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.26),inset_0_-28px_42px_rgba(0,0,0,0.22)]', theme.panel)}>
        <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(255,255,255,0.2),transparent_32%,rgba(255,255,255,0.08)_66%,transparent),radial-gradient(circle_at_16%_14%,rgba(255,255,255,0.16),transparent_20%)]" />
        <div className="absolute inset-y-0 right-0 w-28 bg-[radial-gradient(circle_at_100%_45%,rgba(255,255,255,0.24),transparent_56%)]" />
        <div className="absolute -bottom-8 left-8 h-20 w-52 rounded-full bg-black/24 blur-2xl" />

        <div className="relative z-[1] grid grid-cols-[32%_minmax(0,1fr)] gap-2.5 min-[420px]:grid-cols-[34%_minmax(0,1fr)]">
          <div className="relative min-h-[154px] overflow-hidden rounded-[18px]">
            <div className="absolute inset-x-4 bottom-0 h-5 rounded-full bg-black/42 blur-md" />
            <PlatformKeyVisual platform={theme.key} className="relative h-full min-h-[inherit] w-full ring-1 ring-white/28" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-[18px] font-black leading-tight text-amber-50">{theme.name}</h3>
              <span className="shrink-0 rounded-full border border-white/18 bg-black/20 px-1.5 py-0.5 text-[9px] font-bold text-amber-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] ring-1 ring-white/10">
                {theme.tag}
              </span>
            </div>
            <div className="mt-0.5 truncate text-[20px] font-black leading-tight">{theme.title}</div>
            <p className="mt-0.5 line-clamp-2 text-[12px] font-medium leading-snug text-white/78">{theme.description}</p>

            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={primaryAction.onClick}
                className={cn(
                  'home-premium-button col-span-2 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-white/24 bg-gradient-to-r text-[15px] font-black shadow-[inset_0_1px_0_rgba(255,255,255,0.42),inset_0_-10px_16px_rgba(0,0,0,0.16),0_9px_20px_rgba(0,0,0,0.32)] transition-transform active:scale-[0.99]',
                  theme.action
                )}
              >
                {primaryAction.label}
                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/18 bg-black/72 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_0_10px_rgba(255,255,255,0.16)]">
                  <Play size={14} fill="currentColor" />
                </span>
              </button>

              {secondaryActions.slice(0, 3).map(({ label, icon: ActionIcon, onClick }, index) => (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className={cn(
                    'flex min-h-[38px] items-center justify-center gap-1.5 rounded-xl border border-white/18 bg-[linear-gradient(145deg,rgba(255,255,255,0.18),rgba(0,0,0,0.16)_48%,rgba(0,0,0,0.3))] px-2 text-[11px] font-bold text-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-8px_14px_rgba(0,0,0,0.18)] transition-transform active:scale-[0.98]',
                    secondaryActions.length === 1 && 'col-span-2',
                    secondaryActions.length === 3 && index === 2 && 'col-span-2'
                  )}
                >
                  <ActionIcon size={16} className="shrink-0 text-amber-100" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {pages.length > 0 && (
          <div className="relative z-[1] mt-2.5">
            <div className="mb-1.5 flex items-center gap-1 text-xs font-bold text-amber-50/90">
              <Star size={13} fill="currentColor" />
              {itemLabel}
            </div>
            <Swiper
              slidesPerView={1}
              spaceBetween={0}
              speed={260}
              resistanceRatio={0.68}
              threshold={5}
              onSlideChange={handleSlideChange}
              onSwiper={handleSlideChange}
              className={cn(
                '!overflow-hidden pb-0.5 transition-[height] duration-300 ease-out',
                isExpanded ? '!h-[236px]' : '!h-[74px]'
              )}
            >
              {pages.map((pageItems, pageIndex) => (
                <SwiperSlide key={`swiper-page-${pageIndex}`}>
                  {pageIndex === 0 ? (
                    <div className="grid grid-cols-5 justify-items-center gap-x-3 px-1 min-[420px]:gap-x-4">
                      {pageItems.map((item, index) => (
                        <ShowcaseItemButton
                          key={`${item.key}-${index}`}
                          item={item}
                          index={index}
                          theme={theme}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 grid-rows-[repeat(3,74px)] justify-items-center gap-x-3 gap-y-2 px-1 min-[420px]:gap-x-4">
                      {pageItems.map((item, index) => (
                        <ShowcaseItemButton
                          key={`${item.key}-${(pageIndex - 1) * 15 + index + 5}`}
                          item={item}
                          index={(pageIndex - 1) * 15 + index + 5}
                          theme={theme}
                        />
                      ))}
                    </div>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
            {shouldShowScrollHint && (
              <div
                className={cn(
                  'pointer-events-none absolute bottom-0 right-[-7px] flex w-7 items-center justify-center rounded-l-xl bg-gradient-to-l from-black/44 via-black/18 to-transparent transition-[height] duration-300 ease-out',
                  isExpanded ? 'h-[236px]' : 'h-[74px]'
                )}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/18 bg-black/24 text-white shadow-[0_0_18px_rgba(255,255,255,0.18)] backdrop-blur">
                  <ChevronRight size={17} />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
